import { useMutation, useQueryClient } from "@tanstack/react-query";

import { errorHandler } from "#app/monitoring/errorHandler";
import { showErrorToast } from "#app/notifications";
import { invalidateQuoteRequestLineDetails } from "#modules/optimizations/infra/invalidateQuoteRequestLineDetails";
import {
  optimizationsQuery,
  setOptimizationsQueryData,
} from "#modules/optimizations/infra/optimizations.query";
import { withPreservedCompatibleLines } from "#modules/optimizations/infra/preserveCompatibleLines";
import { restoreOptimization } from "#modules/optimizations/infra/restoreOptimization.api";

export const useRestoreOptimization = ({ quoteRequestId }: { quoteRequestId: string }) => {
  const queryClient = useQueryClient();
  const { queryKey } = optimizationsQuery({ quoteRequestId });

  const { mutate, mutateAsync, ...rest } = useMutation({
    mutationFn: restoreOptimization,
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey });
    },
    onError: (err: Error) => {
      showErrorToast({ title: err.message });
      errorHandler(err);
    },
    onSuccess: (updatedOptimization) => {
      setOptimizationsQueryData(queryClient, quoteRequestId, (oldData) =>
        oldData?.map((optimization) =>
          optimization.id === updatedOptimization.id
            ? withPreservedCompatibleLines(optimization, updatedOptimization)
            : optimization,
        ),
      );
      invalidateQuoteRequestLineDetails(
        queryClient,
        updatedOptimization.quoteRequestLines.map((line) => line.id),
      );
    },
  });

  return { restoreOptimization: mutate, restoreOptimizationAsync: mutateAsync, ...rest };
};
